import { getAnalyticsActor, posthog } from './posthog'

const STATES = [
    'received',
    'started',
    'finished',
    'blocked',
    'error',
    'disabled',
    'unsupported',
] as const
const REASONS = [
    'missing_template',
    'missing_browser_api',
    'not_allowed',
    'suspended',
    'synthesis_failed',
    'audio_failed',
] as const
export type StationAudioState = (typeof STATES)[number]
export type StationPlaybackKind = 'tts' | 'chime'
export type StationAudioReason = (typeof REASONS)[number]

export interface StationAudioContext {
    readonly notification_id: string
    report: (
        state: StationAudioState,
        kind?: StationPlaybackKind,
        reason?: StationAudioReason,
    ) => void
}

/** In-memory observer; no transaction or speech payload enters analytics. */
export function createStationAudioContext(): StationAudioContext | undefined {
    try {
        const notification_id = crypto.randomUUID()
        const reported = new Set<StationAudioState>()
        const impersonating = getAnalyticsActor()?.impersonating ?? false
        return {
            notification_id,
            report(state, kind, reason) {
                if (!STATES.includes(state) || reported.has(state)) return
                reported.add(state)
                try {
                    posthog.capture('station_audio_state', {
                        schema_version: 2,
                        tracking_revision: 'targeted_1',
                        notification_id,
                        state,
                        impersonating,
                        ...(kind === 'tts' || kind === 'chime'
                            ? { playback_kind: kind }
                            : {}),
                        ...(reason && REASONS.includes(reason)
                            ? { reason }
                            : {}),
                    })
                } catch {
                    // Analytics must never interrupt audio or the station callback.
                }
            },
        }
    } catch {
        return undefined
    }
}
